import * as React from 'react'
import { ChangeEvent, useCallback } from 'react'
import MuiTextField, {
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField'
import { FieldProps, getIn } from 'formik'

type TextFieldProps = Omit<MuiTextFieldProps, 'name' | 'value' | 'error'>

export interface Props extends FieldProps, TextFieldProps {
  error?: boolean
  helperText?: string
  avoidValidateOnBlur?: boolean
}

export const MuiTextFieldFormik: React.FC<Props> = ({
  form: { isSubmitting, errors, touched, handleChange },
  disabled = isSubmitting,
  field: { onBlur: fieldOnBlur, onChange: fieldOnChange, ...field },
  onBlur: propsOnBlur,
  onChange: propsOnChange,
  avoidValidateOnBlur,
  ...props
}) => {
  const errorText = props.helperText
    ? props.helperText
    : getIn(errors, field.name)
  const error = props?.error
    ? props?.error
    : getIn(errors, field.name) && getIn(touched, field.name)

  const onBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      propsOnBlur?.(event)
      if (!avoidValidateOnBlur) {
        fieldOnBlur(event ?? field.name)
      }
    },
    [field.name, fieldOnBlur, propsOnBlur, avoidValidateOnBlur],
  )

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      propsOnChange?.(event)
      // remove everything but digits, decimal point, negative sign
      if (typeof event.target.value === 'string')
        event.target.value = event.target.value.trimStart()
      handleChange(field.name != 'role' ? (event ?? field.name) : field.name)
    },
    [handleChange, propsOnChange, field.name],
  )

  const textFieldProps = {
    ...field,
    ...props,
    margin: props.margin ?? 'dense',
    disabled,
    error,
    helperText: !!errorText && error ? errorText : props.helperText,
    onBlur,
    onChange,
  }
  return <MuiTextField {...textFieldProps} />
}
MuiTextFieldFormik.displayName = 'MuiTextFieldFormik'
